import { useEffect, useRef, useState } from 'react'

export const COPY_FAILURE_MESSAGE =
  "Couldn't copy. Select the text and press Ctrl+C (Cmd+C on Mac), or try Copy again."

/** Confirm copying only when the browser accepts it, and retain an actionable error on failure. */
export function useClipboardCopy() {
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState<string | null>(null)
  const [isCopying, setIsCopying] = useState(false)
  const pending = useRef(false)
  const mounted = useRef(true)
  const timer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      clearTimeout(timer.current)
    }
  }, [])

  /** Resolves true once the browser accepted the text. */
  const copy = async (text: string): Promise<boolean> => {
    if (pending.current) return false
    pending.current = true
    clearTimeout(timer.current)
    setCopied(false)
    setCopyError(null)
    setIsCopying(true)
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable')
      await navigator.clipboard.writeText(text)
      if (!mounted.current) return true
      setCopied(true)
      timer.current = setTimeout(() => setCopied(false), 1200)
      return true
    } catch {
      if (mounted.current) setCopyError(COPY_FAILURE_MESSAGE)
      return false
    } finally {
      pending.current = false
      if (mounted.current) setIsCopying(false)
    }
  }

  return { copied, copyError, isCopying, copy }
}
